import { clsx } from "clsx"
import { Link, useLocation, useSearchParams } from "react-router-dom"

import type { ContractFlavor } from "~/api/dto/proposal-contract.dto"
import type {
  SolarContractBlockerCode,
  SolarContractLanguage,
  SolarContractReadinessDto,
  SolarProviderMainContractStatus,
} from "~/api/dto/solar-contract.dto"
import useCalculateSolarProposalPricing from "~/api/hooks/useCalculateSolarProposalPricing"
import useRecalculateSolarProposalSavings from "~/api/hooks/useRecalculateSolarProposalSavings"
import useSendSolarContract from "~/api/hooks/useSendSolarContract"
import useSolarContractReadiness from "~/api/hooks/useSolarContractReadiness"
import useUpdateSolarContractLanguage from "~/api/hooks/useUpdateSolarContractLanguage"
import useUpdateSolarProduction from "~/api/hooks/useUpdateSolarProduction"
import { SelectField } from "~/components/ui-components/v2"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  PricingOutOfDateIndicator,
  REFRESH_PRICING_BUSY_LABEL,
  REFRESH_PRICING_LABEL,
} from "../../ProposalPresentation/pricing/pricing-out-of-date"
import {
  ContractFixCommand,
  ContractFixStepLink,
} from "../../ProposalSetup/contract/contract-fix-actions"
import {
  contractReturnTo,
  type ContractPartnerOverrideProps,
  type ContractProviderContractProps,
  type ContractBlockerFixProps,
  type ContractFixStep,
  type ContractInlineFix,
  type ContractKindBundle,
  type ContractLanguageFieldProps,
} from "../../ProposalSetup/contract/contract-kind-bundle"
import {
  concertDateLabel,
  OUTCOME_TONE_CLASS,
  type ConcertOutcomeTone,
} from "../../ProposalSetup/financing/concert/concert-application-copy"
import {
  ConcertSection,
  concertPrimaryButtonClass,
} from "../../ProposalSetup/financing/concert/concert-section"
import { solarPricingErrorMessage } from "../solar-pricing-error"
import {
  SOLAR_SETUP_STEP_LABELS,
  solarProposalPath,
  solarSetupStepPath,
  type SolarSetupStep,
} from "../solar-setup-paths"
import {
  describeRecalculateError,
  RECALCULATE_SAVINGS_BUSY_LABEL,
  RECALCULATE_SAVINGS_LABEL,
} from "../solar-updates"
import { SolarFinancingStatusStep } from "../SolarFinancingStatusStep"

type SolarFixProps = ContractBlockerFixProps<SolarContractReadinessDto>

export const RETRY_PRODUCTION_LABEL = `Retry production`
const RETRY_PRODUCTION_BUSY_LABEL = `Retrying…`
const RETRY_PRODUCTION_ERROR_PROMPT = `Production couldn't be updated. Try again.`
const LANGUAGE_CHANGE_ERROR_PROMPT = `Unable to change the contract language`

/**
 * Setup step that clears a blocker, when the seller can act on it in Vision.
 * A non-Cash Finance Plan and the homeowner are read-only in V2 (the partner
 * editor refuses them; Site Information is a read-out), so those blockers
 * link nowhere rather than to a step that cannot change them.
 */
const FIX_STEP: Partial<Record<SolarContractBlockerCode, SolarSetupStep>> = {
  FINANCE_PLAN_MISSING: `equipment`,
  DESIGN_NOT_READY: `design`,
}

export const OPEN_CONCERT_FINANCING_LABEL = `Open Concert Financing Status`
export const OPEN_SUNGAGE_FINANCING_LABEL = `Open Sungage Financing Status`
export const OPEN_LIGHTREACH_FINANCING_LABEL = `Open LightReach Financing Status`

/** Steps the fix link names differently from the Setup rail. */
const FIX_STEP_LABEL: Partial<Record<SolarSetupStep, string>> = {
  equipment: `Open Finance Partner & Equipment`,
}

export const OPEN_PROVIDER_FINANCING_LABEL = `Open Financing Status`

/** The provider whose main contract Vision's supplemental follows: Concert, LightReach, or not known yet. */
const mainContractProvider = (
  readiness: SolarContractReadinessDto | undefined,
): string | null => readiness?.providerMainContract?.provider ?? null

/**
 * Copy for the provider-owned main contract. Concert sends it inside its
 * loan documents; LightReach sends its own TPO contract. Until the readiness
 * read names the provider, the copy names neither.
 */
export const providerContractCopy = (
  provider: string | null,
): {
  readonly openFinancing: string
  readonly mainContract: string
  readonly override: string
} =>
  provider === `LightReach`
    ? {
        openFinancing: OPEN_LIGHTREACH_FINANCING_LABEL,
        mainContract: `LightReach sends its own contract to the homeowner. The documents below are Powur's supplemental package.`,
        override: `LightReach sends the main contract. Powur's supplemental documents go out from the Contract drawer once LightReach's contract is sent.`,
      }
    : provider === `Concert`
      ? {
          openFinancing: OPEN_CONCERT_FINANCING_LABEL,
          mainContract: `Concert sends the main contract with its loan documents. The documents below are Powur's supplemental package.`,
          override: `Concert sends the main contract with its loan documents. Powur’s supplemental documents go out from the Contract drawer once those are sent.`,
        }
      : {
          openFinancing: OPEN_PROVIDER_FINANCING_LABEL,
          mainContract: `${provider ?? `The finance partner`} sends the main contract. The documents below are Powur's supplemental package.`,
          override: `The finance partner sends the main contract. Powur's supplemental documents go out from the Contract drawer once it is sent.`,
        }

/** The provider's Financing Status, where its main contract is sent. */
const ProviderFinancingFix = ({
  proposalId,
  readiness,
}: SolarFixProps): JSX.Element => (
  <ContractFixStepLink
    step={{
      path: solarSetupStepPath(proposalId, `financing-status`),
      label: providerContractCopy(mainContractProvider(readiness)).openFinancing,
    }}
  />
)

/** Concert's loan documents, or LightReach's contract, go out from its Financing Status. */
const PROVIDER_CONTRACT_FIX: ContractInlineFix<SolarContractReadinessDto> = {
  available: (readiness) => readiness.actions.viewerCanUpdate,
  Action: ProviderFinancingFix,
}

const solarFixStep = (
  proposalId: string,
  code: SolarContractBlockerCode,
): ContractFixStep | null => {
  const step = FIX_STEP[code]
  return step
    ? {
        path: solarSetupStepPath(proposalId, step),
        label: FIX_STEP_LABEL[step] ?? `Open ${SOLAR_SETUP_STEP_LABELS[step]}`,
      }
    : null
}

const RefreshPricingFix = ({
  proposalId,
  readiness,
  onSettled,
}: SolarFixProps): JSX.Element | null => {
  const refreshPricing = useCalculateSolarProposalPricing()
  if (!readiness.actions.canRefreshPricing) {
    return null
  }
  return (
    <ContractFixCommand
      label={REFRESH_PRICING_LABEL}
      busyLabel={REFRESH_PRICING_BUSY_LABEL}
      busy={refreshPricing.isLoading}
      errorMessage={
        refreshPricing.isError
          ? solarPricingErrorMessage(refreshPricing.error)
          : null
      }
      onRun={() => refreshPricing.mutate({ proposalId }, { onSettled })}
    />
  )
}

const RecalculateSavingsFix = ({
  proposalId,
  readiness,
  onSettled,
}: SolarFixProps): JSX.Element | null => {
  const recalculate = useRecalculateSolarProposalSavings()
  if (!readiness.actions.canRefreshSavings) {
    return null
  }
  return (
    <ContractFixCommand
      label={RECALCULATE_SAVINGS_LABEL}
      busyLabel={RECALCULATE_SAVINGS_BUSY_LABEL}
      busy={recalculate.isLoading}
      errorMessage={
        recalculate.isError ? describeRecalculateError(recalculate.error) : null
      }
      onRun={() => recalculate.mutate({ proposalId }, { onSettled })}
    />
  )
}

const RetryProductionFix = ({
  proposalId,
  readiness,
  onSettled,
}: SolarFixProps): JSX.Element | null => {
  const production = useUpdateSolarProduction()
  if (!readiness.actions.canRetryProduction) {
    return null
  }
  return (
    <ContractFixCommand
      label={RETRY_PRODUCTION_LABEL}
      busyLabel={RETRY_PRODUCTION_BUSY_LABEL}
      busy={production.isLoading}
      errorMessage={
        production.isError
          ? parseAxiosError(production.error, RETRY_PRODUCTION_ERROR_PROMPT)
          : null
      }
      onRun={() => production.mutate({ proposalId }, { onSettled })}
    />
  )
}

const REFRESH_PRICING_FIX: ContractInlineFix<SolarContractReadinessDto> = {
  available: (readiness) => readiness.actions.canRefreshPricing,
  Action: RefreshPricingFix,
}

/** The Credit Human Loan Product step, which picks the loan term. */
const solarLoanProductStepPath = (proposalId: string): string =>
  solarSetupStepPath(proposalId, `loan-product`)

/**
 * Withheld financing terms. Concert: its Financing Status, where the offer
 * and approval live. Sungage: its Financing Status, where the application is
 * started, resumed and refreshed until Sungage approves the Quote, or Refresh
 * Pricing first while an unapproved Quote is stale (an approved one's moved
 * price needs a Change Order, so the server offers only the drawer, as
 * Concert's). Credit Human: Refresh Pricing writes
 * a current Quote in place, unless pricing refuses the plan: Finance Partner &
 * Equipment once the partner isn't offered, the Loan Product step when no term
 * can be priced.
 */
const FinancingTermsFix = (props: SolarFixProps): JSX.Element | null => {
  // Sungage's approval lives in its Financing Status drawer.
  if (props.readiness.actions.canOpenSungageFinancing === true) {
    return (
      <ContractFixStepLink
        step={{
          path: solarSetupStepPath(props.proposalId, `financing-status`),
          label: OPEN_SUNGAGE_FINANCING_LABEL,
        }}
      />
    )
  }
  // Concert's terms come from its offer and approval, LightReach's from its
  // offer and approval, each in its Financing Status.
  if (props.readiness.actions.canOpenConcertFinancing === true) {
    return <ProviderFinancingFix {...props} />
  }
  if (props.readiness.actions.canChooseFinancePartner) {
    return (
      <ContractFixStepLink
        step={{
          path: solarSetupStepPath(props.proposalId, `equipment`),
          label: `Choose a finance partner`,
        }}
      />
    )
  }
  if (props.readiness.actions.canChooseLoanProduct) {
    return (
      <ContractFixStepLink
        step={{
          path: solarLoanProductStepPath(props.proposalId),
          label: `Choose a loan term`,
        }}
      />
    )
  }
  return <RefreshPricingFix {...props} />
}

const FINANCING_TERMS_FIX: ContractInlineFix<SolarContractReadinessDto> = {
  available: (readiness) =>
    readiness.actions.canOpenConcertFinancing === true ||
    readiness.actions.canOpenSungageFinancing === true ||
    readiness.actions.canRefreshPricing ||
    readiness.actions.canChooseLoanProduct ||
    readiness.actions.canChooseFinancePartner,
  Action: FinancingTermsFix,
}

const RECALCULATE_SAVINGS_FIX: ContractInlineFix<SolarContractReadinessDto> = {
  available: (readiness) => readiness.actions.canRefreshSavings,
  Action: RecalculateSavingsFix,
}

type LanguageOption = {
  readonly value: SolarContractLanguage
  readonly label: string
}

// One object per language, so the listbox marks the saved one as selected.
const LANGUAGE_OPTION: Readonly<Record<SolarContractLanguage, LanguageOption>> =
  {
    en: { value: `en`, label: `English` },
    es: { value: `es`, label: `Español` },
  }

const LANGUAGE_OPTIONS: LanguageOption[] = [
  LANGUAGE_OPTION.en,
  LANGUAGE_OPTION.es,
]

/**
 * The package's language. The answer to a change is the new readiness (its
 * template follows the language). The server locks it once a package is out
 * or the selections are locked, and says why.
 */
const SolarContractLanguageField = ({
  proposalId,
  readiness,
}: ContractLanguageFieldProps<SolarContractReadinessDto>): JSX.Element => {
  const update = useUpdateSolarContractLanguage()
  const current = LANGUAGE_OPTION[readiness.language]

  if (!readiness.actions.viewerCanUpdate) {
    return <>{current.label}</>
  }

  return (
    <div className="flex max-w-56 flex-col gap-1.5">
      <SelectField<LanguageOption>
        label="Contract language"
        labelSrOnly
        portal={false}
        disabled={!readiness.actions.canChangeLanguage || update.isLoading}
        options={LANGUAGE_OPTIONS}
        value={current}
        getKey={(option) => option.value}
        getLabel={(option) => option.label}
        onChange={(option) => {
          if (option.value !== readiness.language) {
            update.mutate({ proposalId, language: option.value })
          }
        }}
      />
      {!readiness.actions.canChangeLanguage &&
      readiness.languageLockReason != null ? (
        <p
          data-testid="solar-contract-language-locked"
          className="text-[length:var(--step--2)] text-bp-text-muted"
        >
          {readiness.languageLockReason}
        </p>
      ) : null}
      {update.isError ? (
        <p role="alert" className="text-[length:var(--step--2)] text-bp-danger">
          {parseAxiosError(update.error, LANGUAGE_CHANGE_ERROR_PROMPT)}
        </p>
      ) : null}
    </div>
  )
}

const SolarPricingOutOfDateIndicator = (): JSX.Element => (
  <PricingOutOfDateIndicator testId="solar-contract-pricing-out-of-date" />
)

export const SUPPLEMENTAL_PACKAGE_PARAM = `supplemental`
export const OPEN_SUPPLEMENTAL_CONTRACT_LABEL = `Open Powur supplemental contract`

const PROVIDER_STATUS_COPY: Record<
  SolarProviderMainContractStatus,
  { label: string; tone: ConcertOutcomeTone }
> = {
  not_sent: { label: `Not sent`, tone: `muted` },
  sent: { label: `Sent for signature`, tone: `gold` },
  signed: { label: `Signed`, tone: `success` },
}

/**
 * The provider's main contract: Concert's, inside its loan documents, or
 * LightReach's own. Vision only reads its status; the supplemental package
 * below is Vision's to send.
 */
const SolarProviderMainContract = ({
  readiness,
}: ContractProviderContractProps<SolarContractReadinessDto>): JSX.Element | null => {
  const main = readiness.providerMainContract
  if (main == null) return null
  const copy = PROVIDER_STATUS_COPY[main.status]
  return (
    <ConcertSection
      title={`${main.provider} main contract`}
      testId="solar-provider-main-contract"
      trailing={
        <span
          data-testid="solar-provider-main-contract-status"
          className={clsx(
            `inline-flex items-center rounded-full border px-2.5 py-0.5 text-[length:var(--step--2)] font-semibold`,
            OUTCOME_TONE_CLASS[copy.tone],
          )}
        >
          {copy.label}
        </span>
      }
    >
      <p className="px-4 py-3 text-[length:var(--step--2)] text-bp-text-muted">
        {providerContractCopy(main.provider).mainContract}
        {main.sentAt != null ? ` Sent ${concertDateLabel(main.sentAt)}.` : ``}
        {main.signedAt != null
          ? ` Signed ${concertDateLabel(main.signedAt)}.`
          : ``}
      </p>
    </ConcertSection>
  )
}

/**
 * The provider's Financing Status on the Contract route, while its main
 * contract (Concert's loan documents, or LightReach's contract) hasn't gone
 * out, with the way to Powur's supplemental package beside it.
 */
const SolarConcertContractOverride = ({
  proposalId,
  onClose,
}: ContractPartnerOverrideProps): JSX.Element => {
  const { state } = useLocation()
  const provider = mainContractProvider(
    useSolarContractReadiness(proposalId).data,
  )
  return (
    <SolarFinancingStatusStep proposalId={proposalId} onClose={onClose}>
      <ConcertSection
        title="Powur supplemental contract"
        testId="solar-concert-supplemental-contract"
      >
        <div className="flex flex-col items-start gap-3 px-4 py-4">
          <p className="text-[length:var(--step--1)] text-bp-text-muted">
            {providerContractCopy(provider).override}
          </p>
          <Link
            to={`?package=${SUPPLEMENTAL_PACKAGE_PARAM}`}
            state={state}
            className={concertPrimaryButtonClass}
          >
            {OPEN_SUPPLEMENTAL_CONTRACT_LABEL}
          </Link>
        </div>
      </ConcertSection>
    </SolarFinancingStatusStep>
  )
}

/**
 * Contract → Status for a Concert plan opens its Financing Status until the
 * loan documents go out, as Battery's opens its application status, unless
 * the seller asked for the supplemental package. Cash and Credit Human have
 * no partner-run drawer.
 */
const useSolarPartnerOverride = (
  proposalId: string | undefined,
): typeof SolarConcertContractOverride | null => {
  const [searchParams] = useSearchParams()
  const main = useSolarContractReadiness(proposalId).data?.providerMainContract
  return main?.status === `not_sent` &&
    searchParams.get(`package`) !== SUPPLEMENTAL_PACKAGE_PARAM
    ? SolarConcertContractOverride
    : null
}

/**
 * Powur's supplemental package, which is all Vision sends when the provider
 * sends the main contract (Concert's loan documents, LightReach's contract).
 * Never a Solar contract: Contracting leaves Powur's out of the package.
 */
export const SUPPLEMENTAL_PACKAGE_LABEL: Readonly<
  Record<ContractFlavor, string>
> = {
  standard: `Powur supplemental package`,
  participate: `Participate supplemental package`,
}

/** Solar Proposal reads, commands and fixes for the shared Contract drawer. */
export const solarContractBundle: ContractKindBundle<SolarContractReadinessDto> =
  {
    flavorLabel: {
      standard: `Standard Solar contract`,
      participate: `Participate Solar contract`,
    },
    supplementalPackage: {
      label: SUPPLEMENTAL_PACKAGE_LABEL,
      isNext: (readiness) => readiness.providerMainContract != null,
    },
    proposalPath: solarProposalPath,
    returnTo: (state, fallback) =>
      contractReturnTo(state, fallback, `/solar-proposal/`),
    useReadiness: useSolarContractReadiness,
    useSend: useSendSolarContract,
    inlineFixes: {
      PRICING_NOT_CALCULATED: REFRESH_PRICING_FIX,
      PRICING_OUT_OF_DATE: REFRESH_PRICING_FIX,
      FINANCING_TERMS_UNAVAILABLE: FINANCING_TERMS_FIX,
      LOAN_DOCUMENTS_NOT_SENT: PROVIDER_CONTRACT_FIX,
      SAVINGS_NOT_CALCULATED: RECALCULATE_SAVINGS_FIX,
      SAVINGS_OUT_OF_DATE: RECALCULATE_SAVINGS_FIX,
      PRODUCTION_NOT_UPDATED: {
        available: (readiness) => readiness.actions.canRetryProduction,
        Action: RetryProductionFix,
      },
    },
    fixStep: solarFixStep,
    PricingOutOfDateIndicator: SolarPricingOutOfDateIndicator,
    LanguageField: SolarContractLanguageField,
    usePartnerOverride: useSolarPartnerOverride,
    ProviderMainContract: SolarProviderMainContract,
  }
